import React, { useState, useRef, useEffect } from 'react';
import { BiHelpCircle, BiXCircle, BiTrophy, BiBullseye, BiBoltCircle, BiSolidFlame, BiBookReader, BiJoystick } from 'react-icons/bi';
import { FaTrophy, FaMedal, FaAward } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import LeaderboardRowMessage from './leaderboard/LeaderboardRowMessage';
import OutputBattleItemStrip from './leaderboard/OutputBattleItems';
// The flag sprite CSS was only imported by CountrySelector (profile/sign-up chunks),
// so on the dashboard the `fi fi-xx` spans had no styles and the flags were blank.
import 'flag-icons/css/flag-icons.min.css';

// The /leaderboards page reveals each board (up to the stored 100) in steps.
const FULL_PAGE_STEP = 25;

// currentPage: 'home' (dashboard: a short board, "Show more" expands it in
// place via onSeeMore/isExpanded) or 'leaderboards' (the full page).
// highlightUsername marks the signed-in learner's own row.
function Leaderboard({ currentPage, totalAccuracyData = [], speedRunData = [], streakData = [], mostReviewsData = [], outputBattleData = [], onSeeMore = () => {}, isExpanded = false, canExpand = true, highlightUsername = null }) {
  const [activeTab, setActiveTab] = useState('Most Reviews');
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [visibleCount, setVisibleCount] = useState(FULL_PAGE_STEP);

  // Most Reviews leads: it is the default tab, it rewards the behaviour we want
  // (accuracy drops when you push into new material, so it is a poor default),
  // and tabIcons below is index-aligned to this array.
  const tabs = ['Most Reviews', 'Total Accuracy', 'Speed Run', 'Longest Streak', 'Output Battle'];

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setVisibleCount(FULL_PAGE_STEP);
  };

  const toggleTooltip = () => {
    setShowTooltip(!showTooltip);
  };

  const closeOverlay = () => {
    setShowOverlay(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  // Determine which data to display based on activeTab
  let leaderboardData = [];
  if (activeTab === 'Total Accuracy') {
    leaderboardData = totalAccuracyData;
  } else if (activeTab === 'Speed Run') {
    leaderboardData = speedRunData;
  } else if (activeTab === 'Longest Streak') {
    leaderboardData = streakData;
  } else if (activeTab === 'Most Reviews') {
    leaderboardData = mostReviewsData;
  } else if (activeTab === 'Output Battle') {
    leaderboardData = outputBattleData;
  }

  const isFullPage = currentPage === 'leaderboards';
  const displayedData = isFullPage ? leaderboardData.slice(0, visibleCount) : leaderboardData;
  const canLoadMore = isFullPage && displayedData.length < leaderboardData.length;

  // Update overlay based on data availability
  useEffect(() => {
    setShowOverlay(leaderboardData.length === 0);
  }, [leaderboardData]);

  // Function to determine the icon based on rank
  const getIconForRank = (rank) => {
    switch (rank) {
      case 1:
        return <FaTrophy className="text-yellow-400" />;
      case 2:
        return <FaMedal className="text-gray-400" />;
      case 3:
        return <FaMedal className="text-yellow-600" />;
      default:
        return <FaAward className="text-green-500" />;
    }
  };

  // Format the score based on the active tab
  const formatScore = (item) => {
    if (activeTab === 'Total Accuracy') {
      // Convert accuracy value to percentage
      return `${(item.totalAccuracyValue * 100).toFixed(2)}%`;
    } else if (activeTab === 'Speed Run') {
      // Display total review minutes rounded to nearest whole number
      return `${Math.round(item.totalReviewMinutes)} mins`;
    } else if (activeTab === 'Longest Streak') {
      // Display streak days
      return `${item.currentStreak} days`;
    } else if (activeTab === 'Most Reviews') {
      return `${(item.totalReviews || 0).toLocaleString()} reviews`;
    } else if (activeTab === 'Output Battle') {
      return `${(item.score || 0).toLocaleString()} pts`;
    }
    return item.score; // Default case
  };

  return (
    <div className="relative bg-white rounded-xl shadow-blueBoxShadow overflow-hidden">
      {/* Header Section with Gradient */}
      <div className="bg-gradient-to-r from-logoColor to-darkBlueColor text-white p-6 relative max-sm:px-4 max-sm:py-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <BiTrophy className="text-4xl max-sm:text-2xl" />
            <div>
              <h2 className="text-3xl font-bold max-sm:text-xl">Leaderboard</h2>
            </div>
          </div>
          <BiHelpCircle
            className="text-2xl cursor-pointer transition-transform hover:scale-110 opacity-80 hover:opacity-100"
            onClick={toggleTooltip}
          />
        </div>
      </div>

      {/* Tooltip */}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="absolute top-20 right-4 bg-white border border-gray-200 rounded-xl p-6 shadow-xl z-50 max-w-sm max-sm:top-14 max-sm:left-3 max-sm:right-3 max-sm:p-4"
        >
          <BiXCircle
            className="absolute top-3 right-3 text-xl text-gray-400 cursor-pointer hover:text-gray-600"
            onClick={toggleTooltip}
          />
          <div className="flex items-center gap-2 mb-3">
            <BiTrophy className="text-xl text-logoColor" />
            <h3 className="text-logoColor font-bold text-lg">About Rankings</h3>
          </div>
          <ul className="text-gray-600 text-sm space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-darkBlueColor">•</span>
              <span>Navigate to "My Profile" to change your username.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-immersionHubGreen">•</span>
              <span>Most Reviews: Total reviews completed — accuracy breaks ties.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-darkOrangeColor">•</span>
              <span>Total Accuracy: Your overall accuracy, once you have 500+ reviews behind it.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-oniKanjiPink">•</span>
              <span>Speed Run: Users with the fastest review times.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-coral">•</span>
              <span>Longest Streak: Consecutive days of reviewing.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-logoColor">•</span>
              <span>Output Battle: Best run in the sentence-battle roguelite — hover the icons to see the kit they won with.</span>
            </li>
          </ul>
        </div>
      )}

      {/* Modern Tab Navigation */}
      <div className="p-6 pb-0 max-sm:px-3 max-sm:pt-3">
        <div className="bg-gray-50 rounded-xl p-2 max-sm:p-1">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 max-sm:grid-cols-5 max-sm:gap-1 sm:max-md:grid-cols-5">
            {tabs.map((tab, index) => {
              const tabIcons = [BiBookReader, BiBullseye, BiBoltCircle, BiSolidFlame, BiJoystick];
              const IconComponent = tabIcons[index];
              return (
                <button
                  key={tab}
                  className={`p-2 sm:p-3 rounded-lg font-semibold text-xs sm:text-sm transition-all duration-300 transform hover:scale-105 max-sm:px-0.5 max-sm:py-1.5 max-sm:min-h-[44px] max-sm:hover:scale-100 ${
                    activeTab === tab
                      ? 'bg-gradient-to-r from-logoColor to-darkBlueColor text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-100 hover:text-logoColor'
                  }`}
                  onClick={() => handleTabClick(tab)}
                >
                  <div className="flex items-center justify-center gap-1 sm:gap-2 max-sm:flex-col max-sm:gap-0.5">
                    <IconComponent className="text-sm sm:text-lg max-sm:text-base" />
                    <span className="hidden sm:inline">{tab.replace(' ', '\n')}</span>
                    <span className="sm:hidden text-xs max-sm:text-[10px] max-sm:leading-none">{tab.split(' ')[0]}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Leaderboard Content */}
      <div className="p-6 max-sm:p-3">
        {leaderboardData.length === 0 ? (
          <div className="text-center py-12">
            <BiTrophy className="text-6xl text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No data available yet</p>
            <p className="text-gray-400 text-sm mt-2">Check back after the next update!</p>
          </div>
        ) : (
          <div className="space-y-3 max-sm:space-y-2">
            {displayedData.map((item, index) => {
              const isTop3 = item.position <= 3;
              const isMe = !!highlightUsername && item.publicUsername === highlightUsername;
              const bgGradients = {
                1: 'bg-gradient-to-r from-coral to-coralHover border-coral',
                2: 'bg-gradient-to-r from-lightBlueBackground to-softBlue border-darkBlueColor', 
                3: 'bg-gradient-to-r from-lightOrangeBackground to-darkOrangeColorButtonHover border-darkOrangeColor'
              };
              
              return (
                <div
                  key={item.position}
                  className={`p-3 sm:p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg border-2 max-sm:px-2.5 max-sm:py-2 max-sm:hover:scale-100 ${
                    isTop3
                      ? bgGradients[item.position]
                      : 'bg-gradient-to-r from-white to-gray-50 border-gray-200 hover:border-logoColor'
                  } ${isMe ? 'ring-4 ring-darkOrangeColor ring-offset-1' : ''}`}
                >
                  <div className="grid gap-2 sm:gap-4 grid-cols-[auto_1fr_auto] sm:grid-cols-[auto_1fr_140px_auto] items-center">
                    {/* Column 1: Rank Icon + Flag + Name */}
                    <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
                      <div className={`flex items-center justify-center w-8 h-8 sm:w-12 sm:h-12 rounded-full flex-shrink-0 ${
                        isTop3 ? 'bg-white shadow-md' : 'bg-gray-100'
                      }`}>
                        {getIconForRank(item.position)}
                      </div>

                      {/* Country Flag - Only for user leaderboards (not kanji) */}
                      {item.country && (
                        <span
                          className={`fi fi-${item.country.toLowerCase()} flex-shrink-0`}
                          style={{ fontSize: '1.1rem' }}
                          title={item.country}
                        />
                      )}

                      {/* Name/Kanji */}
                      <div className="min-w-0 flex-shrink">
                        <div
                          className="font-bold truncate text-xs sm:text-lg text-logoColor max-w-[80px] sm:max-w-none max-sm:text-sm max-sm:max-w-none"
                          title={item.publicUsername}
                        >
                          {item.publicUsername}
                        </div>
                        <div className="text-[10px] sm:text-sm text-gray-500 whitespace-nowrap">
                          #{item.position} • Lvl {item.currentLevel}
                          {isMe && (
                            <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-darkOrangeColor text-white text-[10px] font-bold">You</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Column 2: item strip for Output Battle, spacer otherwise (desktop only) */}
                    <div className="hidden sm:block">
                      {activeTab === 'Output Battle' && <OutputBattleItemStrip items={item.items} />}
                    </div>

                    {/* Column 3: Leaderboard Message - ALWAYS rendered to maintain grid alignment (desktop only) */}
                    <div className="hidden sm:flex justify-center items-center min-w-[140px]">
                      <LeaderboardRowMessage
                        leaderboardEntry={item}
                        position={item.position}
                      />
                    </div>

                    {/* Column 4: Stats */}
                    <div className="flex flex-col items-end text-right max-sm:shrink-0">
                      <div className={`text-base sm:text-xl font-bold ${isTop3 ? 'text-black' : 'text-darkBlueColor'} max-sm:text-[15px] max-sm:whitespace-nowrap`}>
                        {formatScore(item)}
                      </div>
                      {activeTab === 'Most Reviews' && (
                        <div className={`text-[10px] sm:text-sm ${isTop3 ? 'text-black' : 'text-gray-600'}`}>
                          {(item.totalAccuracyValue * 100).toFixed(1)}% accuracy
                        </div>
                      )}
                      {activeTab === 'Output Battle' && (
                        <div className={`text-[10px] sm:text-sm ${isTop3 ? 'text-black' : 'text-gray-600'}`}>
                          {item.won ? `${item.levelReached} cleared ✓` : `reached ${item.levelReached}`} • {item.bossesDown} oni
                        </div>
                      )}

                      {/* Mobile: Output Battle item strip below stats */}
                      {activeTab === 'Output Battle' && (
                        <div className="sm:hidden mt-2">
                          <OutputBattleItemStrip items={item.items} />
                        </div>
                      )}

                      {/* Mobile: Message button below stats */}
                      <div className="sm:hidden mt-2">
                        <LeaderboardRowMessage
                          leaderboardEntry={item}
                          position={item.position}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Full page: reveal the rest of the board in steps */}
      {canLoadMore && (
        <div className="px-6 pb-6 max-sm:px-3 max-sm:pb-3">
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={() => setVisibleCount((n) => n + FULL_PAGE_STEP)}
              className="w-full bg-gradient-to-r from-gray-100 to-gray-200 text-logoColor py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 border border-gray-300 flex items-center justify-center gap-2"
            >
              <BiTrophy className="text-lg" />
              <span>Show more</span>
            </button>
          </div>
        </div>
      )}

      {/* Dashboard: expand in place, with the full page one tap away */}
      {currentPage === 'home' && (
        <div className="px-6 pb-6 max-sm:px-3 max-sm:pb-3">
          <div className="border-t border-gray-200 pt-4 flex flex-col sm:flex-row gap-3">
            {(canExpand || isExpanded) && (
              <button
                onClick={onSeeMore}
                className="flex-1 bg-gradient-to-r from-gray-100 to-gray-200 text-logoColor py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 border border-gray-300 flex items-center justify-center gap-2"
              >
                <BiTrophy className="text-lg" />
                <span>{isExpanded ? 'Show less' : 'Show more'}</span>
              </button>
            )}
            <Link
              to="/leaderboards"
              className="flex-1 bg-gradient-to-r from-logoColor to-darkBlueColor text-white py-3 px-6 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              <span>Your standing &amp; full leaderboards →</span>
            </Link>
          </div>
        </div>
      )}
</div>
);
}

Leaderboard.propTypes = {
  currentPage: PropTypes.string.isRequired,
  totalAccuracyData: PropTypes.array,
  speedRunData: PropTypes.array,
  streakData: PropTypes.array,
  mostReviewsData: PropTypes.array,
  outputBattleData: PropTypes.array,
  onSeeMore: PropTypes.func,
  isExpanded: PropTypes.bool,
  canExpand: PropTypes.bool,
  highlightUsername: PropTypes.string,
};

export default Leaderboard;
